import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaChartLine, FaUser, FaUsers, FaComments, FaStar } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { useAuth } from '../../../lib/AuthProvider';
import './ReceiveFeedback.css';

interface StudentFeedback {
  id: string;
  student_id: string;
  student_name: string;
  supervisor_id: string;
  supervisor_name: string;
  rating: number;
  comment: string;
  created_at: string;
  type: 'student_to_supervisor';
}

interface SupervisorFeedback {
  id: string;
  supervisor_id: string;
  supervisor_name: string;
  trainee_id: string;
  trainee_name: string;
  rating: number;
  comment: string;
  created_at: string;
  type: 'supervisor_to_student';
}

type FeedbackItem = StudentFeedback | SupervisorFeedback;

const ReceiveFeedback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'student' | 'supervisor'>('all');
  const [feedbackList, setFeedbackList] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFeedbackData();
  }, []);

  const loadFeedbackData = async () => {
    setLoading(true);
    
    // Static demo data - replace with actual database queries when tables are ready
    const demoStudentFeedback: StudentFeedback[] = [
      {
        id: '1',
        student_id: 'STU001',
        student_name: 'Alice Garcia',
        supervisor_id: 'SUP001',
        supervisor_name: 'Ophir Jemuel Baclig',
        rating: 5,
        comment: 'Great supervisor! Very helpful and always available for questions. Learned a lot during my internship.',
        created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'student_to_supervisor'
      },
      {
        id: '2',
        student_id: 'STU002',
        student_name: 'Mark Adrian Dumlao',
        supervisor_id: 'SUP002',
        supervisor_name: 'Jayson Rabang',
        rating: 4,
        comment: 'Good experience overall. Could use more structured feedback sessions.',
        created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'student_to_supervisor'
      }
    ];

    const demoSupervisorFeedback: SupervisorFeedback[] = [
      {
        id: '3',
        supervisor_id: 'SUP001',
        supervisor_name: 'Florida Ursulom',
        trainee_id: 'STU003',
        trainee_name: 'John Lloyd Micua',
        rating: 5,
        comment: 'Excellent work ethic and quick learner. Always on time and completes tasks efficiently.',
        created_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'supervisor_to_student'
      },
      {
        id: '4',
        supervisor_id: 'SUP002',
        supervisor_name: 'Robinson Tollo',
        trainee_id: 'STU004',
        trainee_name: 'Jan Ramese Paz',
        rating: 3,
        comment: 'Needs improvement in time management. Shows potential but requires more guidance.',
        created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        type: 'supervisor_to_student'
      }
    ];

    const allFeedback = [...demoStudentFeedback, ...demoSupervisorFeedback].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    setFeedbackList(allFeedback);
    setLoading(false);
  };

  const getFilteredFeedback = () => {
    switch (activeTab) {
      case 'student':
        return feedbackList.filter(f => f.type === 'student_to_supervisor');
      case 'supervisor':
        return feedbackList.filter(f => f.type === 'supervisor_to_student');
      default:
        return feedbackList;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderStars = (rating: number) => {
    return (
      <div className="stars-display">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar key={star} className={`star ${star <= rating ? 'filled' : 'empty'}`} />
        ))}
        <span className="rating-value">{rating}/5</span>
      </div>
    );
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/supervisor');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="coordinator-home">
      <header className="top-nav">
        <div className="nav-container">
          <div className="logo">
            <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
          </div>
          <button
            className="hamburger-btn"
            aria-label="Open menu"
            aria-controls="ojt-sidebar"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(true)}
          >
            {'\u2630'}
          </button>
          <div className="nav-links">
            <button 
              onClick={() => navigate('/ojt-coordinator/home')} 
              className={`nav-link ${isActive('/ojt-coordinator/home') ? 'active' : ''}`}
            >
              <FaHome className="nav-icon" /> Home
            </button>
            <button 
              onClick={() => navigate('/ojt-coordinator/trainees')} 
              className={`nav-link ${isActive('/ojt-coordinator/trainees') ? 'active' : ''}`}
            >
              <FaUsers className="nav-icon" /> Trainees
            </button>
            <button 
              onClick={() => navigate('/ojt-coordinator/feedback')} 
              className={`nav-link ${isActive('/ojt-coordinator/feedback') ? 'active' : ''}`}
            >
              <FaComments className="nav-icon" /> Feedback
            </button>
            <button className="nav-link">
              <FaChartLine className="nav-icon" /> Analytics
            </button>
            <button className="nav-link">
              <FaUser className="nav-icon" /> Profile
            </button>
          </div>
          <button onClick={handleSignOut} className="nav-link logout-button">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="ojt-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <button 
            onClick={() => navigate('/ojt-coordinator/home')} 
            className="nav-link"
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/ojt-coordinator/trainees')} 
            className="nav-link"
          >
            <FaUsers className="nav-icon" /> Trainees
          </button>
          <button 
            onClick={() => navigate('/ojt-coordinator/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button className="nav-link">
            <FaChartLine className="nav-icon" /> Analytics
          </button>
          <button className="nav-link">
            <FaUser className="nav-icon" /> Profile
          </button>
        </nav>
        <div className="sidebar-footer">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              handleSignOut();
            }} 
            className="sidebar-logout-button"
          >
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        {/* Filter Tabs */}
        <div className="feedback-tabs">
          <button 
            className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Feedback ({feedbackList.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'student' ? 'active' : ''}`}
            onClick={() => setActiveTab('student')}
          >
            From Students ({feedbackList.filter(f => f.type === 'student_to_supervisor').length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'supervisor' ? 'active' : ''}`}
            onClick={() => setActiveTab('supervisor')}
          >
            From Supervisors ({feedbackList.filter(f => f.type === 'supervisor_to_student').length})
          </button>
        </div>

        {/* Feedback List */}
        <div className="feedback-list-container">
          {loading ? (
            <div className="loading-state">
              <p>Loading feedback...</p>
            </div>
          ) : getFilteredFeedback().length === 0 ? (
            <div className="empty-state">
              <FaComments className="empty-icon" />
              <h3>No feedback found</h3>
              <p>There is no feedback to display at this time.</p>
            </div>
          ) : (
            <div className="feedback-cards">
              {getFilteredFeedback().map((feedback) => (
                <div key={feedback.id} className={`feedback-card ${feedback.type}`}>
                  <div className="feedback-header">
                    <div className="feedback-type-badge">
                      {feedback.type === 'student_to_supervisor' ? (
                        <span className="badge student">Student → Supervisor</span>
                      ) : (
                        <span className="badge supervisor">Supervisor → Student</span>
                      )}
                    </div>
                    <div className="feedback-date">
                      {formatDate(feedback.created_at)}
                    </div>
                  </div>
                  
                  <div className="feedback-participants">
                    {feedback.type === 'student_to_supervisor' ? (
                      <>
                        <div className="participant">
                          <span className="label">From:</span>
                          <span className="name">{(feedback as StudentFeedback).student_name}</span>
                        </div>
                        <div className="participant">
                          <span className="label">To:</span>
                          <span className="name">{(feedback as StudentFeedback).supervisor_name}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="participant">
                          <span className="label">From:</span>
                          <span className="name">{(feedback as SupervisorFeedback).supervisor_name}</span>
                        </div>
                        <div className="participant">
                          <span className="label">To:</span>
                          <span className="name">{(feedback as SupervisorFeedback).trainee_name}</span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="feedback-rating">
                    {renderStars(feedback.rating)}
                  </div>

                  <div className="feedback-comment">
                    <p>"{feedback.comment}"</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ReceiveFeedback;
